<template>
  <vl-grid>
    <vl-column width="12" width-xs="12">
      <vl-title tag-name="h3">
        {{ title }}
      </vl-title>
    </vl-column>
    <vl-column width="12" width-xs="12">
      <vl-data-table-extended
        :data="tableItems"
        :columns="columnMap"
        :fetching="loading"
        :meta="metaData"
        mod-clickable-rows
        mod-zebra
      >
        <template v-if="!loading" v-slot:noresults>
          <div v-vl-align:left class="vl-u-spacer--large">
            <vl-annotation>{{ noResultsText }}</vl-annotation>
          </div>
        </template>
      </vl-data-table-extended>
    </vl-column>
  </vl-grid>
</template>
<script lang="ts" setup>
import { ColumnDefExtended } from '@/components/vl-data-table-extended/types';
import { RowData } from '@tanstack/vue-table';
import { PropType, computed, reactive } from 'vue';

type EntityType =
  | 'deliveryprocessdefinition'
  | 'productprocessdefinition'
  | 'processtrigger'
  | 'deliveryrun'
  | 'productrun'
  | 'productgroup';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  noResultsText: {
    type: String,
    default: 'Geen resultaten gevonden',
  },
  items: {
    type: Array as PropType<any[]>,
    required: true,
  },
  entityType: {
    type: String as PropType<EntityType>,
    required: true,
  },
  labelAccessorKey: {
    type: String,
    default: 'label',
  },
  columns: {
    type: Array as PropType<ColumnDefExtended<RowData>[]>,
    required: false,
    default: () => [],
  },
  metaData: {
    type: Object as PropType<{ currentPage?: number; resultsPerPage?: number }>,
    required: false,
    default: () => ({
      currentPage: 1,
      resultsPerPage: 5,
    }),
  },
});

const data = reactive({
  isLoading: false,
});

const loading = computed(() => {
  return data.isLoading;
});

const metaData = reactive({
  totalRows: 0,
  currentPage: props.metaData?.currentPage || 1,
  resultsPerPage: props.metaData?.resultsPerPage || 5,
  serverside: false,
});

const tableItems = computed((): any & { routerLink?: string }[] => {
  return props.items?.map((i) => {
    return {
      ...i,
      routerLink: createRouterLink(i.id),
    };
  });
});

const columnMap = computed((): ColumnDefExtended<RowData>[] => {
  if (props.columns?.length) {
    return props.columns;
  }

  return [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true,
      size: 5,
      link: true,
    },
    {
      accessorKey: props.labelAccessorKey || 'label',
      header: 'Label',
      enableSorting: true,
      sortDescFirst: false,
      size: 30,
      link: true,
    },
  ];
});

const createRouterLink = (id: number): string => {
  if (props.entityType === 'deliveryprocessdefinition') {
    return `/configuration/deliveryprocessdefinition/${id}`;
  }
  if (props.entityType === 'productprocessdefinition') {
    return `/configuration/productprocessdefinition/${id}`;
  }
  if (props.entityType === 'processtrigger') {
    return `/configuration/processtrigger/${id}`;
  }
  if (props.entityType === 'deliveryrun') {
    return `/delivery/${id}`;
  }
  if (props.entityType === 'productrun') {
    return `/product/${id}`;
  }
  if (props.entityType === 'productgroup') {
    return `/configuration/productgroup/${id}`;
  }
  return '';
};
</script>
<style lang="scss" scoped></style>
